import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useMatomo } from '@jonkoops/matomo-tracker-react'
import { toast } from 'sonner'
import { Shield, Lock, User, Loader2, Eye, EyeOff } from 'lucide-react'

export default function LoginPage() {
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)

    const { t } = useTranslation()
    const { login } = useAuth()
    const navigate = useNavigate()
    const { trackEvent } = useMatomo()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)

        try {
            await login(username, password)
            trackEvent({ category: 'Auth', action: 'Login Success' })
            toast.success(t('login.toastSuccess'))
            navigate('/dashboard')
        } catch (error: any) {
            trackEvent({ category: 'Auth', action: 'Login Failed' })
            toast.error(error.response?.data?.message || t('login.toastError'))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#050608] selection:bg-blue-500/30 font-sans relative overflow-hidden">
            {/* Background Decorative Elements */}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 blur-[120px] rounded-full" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/10 blur-[120px] rounded-full" />
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03] pointer-events-none" />

            {/* Login Card */}
            <div className="w-full max-w-lg z-10 p-4">
                <div className="bg-gray-900/40 backdrop-blur-3xl border border-white/10 rounded-[2rem] md:rounded-[3rem] p-6 md:p-10 lg:p-16 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
                    {/* Header/Logo */}
                    <div className="flex flex-col items-center mb-12">
                        <div className="w-20 h-20 rounded-3xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center p-5 shadow-2xl shadow-blue-600/20 mb-6">
                            <Shield className="w-full h-full text-white" />
                        </div>
                        <div className="text-center">
                            <h1 className="text-4xl font-black text-white tracking-tighter uppercase">VANTAK</h1>
                            <div className="flex items-center gap-2 justify-center mt-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_#3b82f6]" />
                                <span className="text-[10px] text-gray-500 font-black uppercase tracking-[0.3em]">{t('login.tagline')}</span>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Username Input */}
                        <div className="space-y-3">
                            <label htmlFor="username" className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">
                                {t('login.operatorId')}
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-600 group-focus-within:text-blue-500 transition-colors" />
                                </div>
                                <input
                                    id="username"
                                    type="text"
                                    required
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="block w-full bg-white/[0.03] border border-white/10 rounded-2xl pl-14 pr-6 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/50 focus:bg-white/[0.05] transition-all"
                                    placeholder={t('login.operatorIdPlaceholder')}
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div className="space-y-3">
                            <div className="flex justify-between items-center px-1">
                                <label htmlFor="password" className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                                    {t('login.accessKey')}
                                </label>
                            </div>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-600 group-focus-within:text-blue-500 transition-colors" />
                                </div>
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="block w-full bg-white/[0.03] border border-white/10 rounded-2xl pl-14 pr-14 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/50 focus:bg-white/[0.05] transition-all"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-5 flex items-center text-gray-600 hover:text-blue-500 transition-colors"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="group relative w-full py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl transition-all duration-300 active:scale-[0.98] shadow-2xl shadow-blue-500/20 disabled:opacity-50 overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                            <span className="relative flex items-center justify-center gap-3 font-black text-xs uppercase tracking-[0.2em]">
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        {t('login.verifying')}
                                    </>
                                ) : (
                                    t('login.submit')
                                )}
                            </span>
                        </button>
                    </form>

                    {/* Footer / Demo Info */}
                    <div className="mt-12 pt-8 border-t border-white/5 text-center">
                        <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest leading-loose">
                            {t('login.restrictedAccess')}
                        </p>
                        <p className="mt-3 text-[10px] text-gray-500">
                            <a href="/terms" className="hover:text-gray-300 underline">Términos</a>
                            <span className="mx-2">·</span>
                            <a href="/privacy" className="hover:text-gray-300 underline">Privacidad</a>
                        </p>
                    </div>
                </div>

                {/* System Status Line */}
                <div className="mt-8 flex justify-center gap-8 opacity-20">
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        <span className="text-[8px] font-black text-white uppercase tracking-widest">Auth Server UP</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        <span className="text-[8px] font-black text-white uppercase tracking-widest">TLS 1.3 Active</span>
                    </div>
                </div>
            </div>
        </div>
    )
}
